import React, { useEffect, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import svg from '../assets/smart_welcome_icon.svg';
import Toggle from '@veneer/core/dist/scripts/toggle';
import LogoHp from '@veneer/core/dist/scripts/logos/hp/hp';
import easyStartproIcon from '../assets/Sehawk_Logo_512.png';
import { propOr } from 'ramda';
import { LEGACY_SUPPORTED_LANGUAGES_KEY, RTL_LANG } from '../constants/i18n';
import { determineLocale } from '../utils/determineLocale';
import { ToggleLabelHeader } from '../styles/privacySettingsStyles';
import { useHistory, useParams } from 'react-router';

import {
  ReTargetContainer,
  ReTargetContentContainer,
  ReTargetBody,
  ReTargetButtonContainer,
  StyledButton,
  Container,
  LogoContainer,
  SureSupplyContainer,
  BodySureSupply,
  Graphic,
  Title,
  Body,
  ToggleLayout,
  BodyList
} from '../styles/welcomeStyles';
import { requiredBasicPrinter } from '../constants/purposeID';
import { renderTrackEvent } from '../lib/analyticsTracking';

import { ConsentScreenDisplayed } from '../utils/simpleUIAnalytics';
import { sendSimpleUiEvent } from '../jweb/plugins/dataCollection';

const SmartWelcome = ({
  handleClick,
  declineAllClick,
  csData = {},
  alpacaData,
  loading,
  declineAllLoading,
  smallButton,
  reTarget,
  query,
  sureSupplyAnalyticsContent = '',
  sureSupplyAnalyticsConsentToggle = false,
  setSureSupplyAnalyticsConsentToggle,
  requiredWelcomeContent = '',
  errorResponse
}) => {
  const errorCode = propOr(0, 'code', errorResponse);
  const errorMessage = propOr('', 'message', errorResponse);
  const title = propOr('', 'title', csData);
  const acceptAllButton = propOr('', 'acceptAllButton', csData);
  const manageOptionsButton = propOr('', 'manageOptionButton', csData);
  const declineAllButton = propOr('', 'declineAllButton', csData);
  const continueButton = propOr('', 'button', csData);
  const continueButtonText = propOr('', 'continueButtonText', csData);
  const body = propOr('', 'body', csData);
  const secondaryBodyText = propOr('', 'secondaryBodyText', csData);
  const supplementalText = propOr('', 'supplementalText', csData);
  const buttonTitle = propOr('', 'buttonTitle', csData);
  const errorText = propOr('', 'errorText', csData);
  const printerImage = propOr('', 'printerImage', csData);
  const history = useHistory();
  const params = useParams();
  const { country, language } = determineLocale(LEGACY_SUPPORTED_LANGUAGES_KEY);
  const { pathname, search } = history.location;
  const { platform, version } = params;
  const [isDesktop, setDesktop] = useState(false);
  const [consentsLocale, setConsentLocaleProps] = useState(null);
  const [isRestOfWorld, setIsRestOfWorld] = useState(null);
  const isRTL = RTL_LANG.includes(language) ? true : false;
  const queryParam = query.appName || '';
  const hasAppNameSureSupply = queryParam.toLowerCase() === 'suresupply';
  const hasAppNameEasyStart = queryParam.toLowerCase() === 'easystart';
  const hasAppNameEasyStartPro = queryParam.toLowerCase() === 'easystartpro';
  const hasAppNameWJA = queryParam.toLowerCase() === 'wja';
  const isWorkFromHomePrinter = query && query.wfh && query.wfh === 'true';
  const userConsents = pathname.includes('user-consents');
  const isCompanyTenantPrinter = query && query.companyTenantId && query.companyTenantId !== '';
  const [isRenderAcceptAllButton, setRenderAcceptAllButton] = useState(false);
  const { checkBasicFuncPrinter, ffAnalyticsTracking } = useFlags();

  useEffect(() => {
    setDesktop(platform === 'ios' || platform === 'android' ? false : true);
    setConsentLocaleProps(determineLocale(LEGACY_SUPPORTED_LANGUAGES_KEY));
    sendSimpleUiEvent(
      ConsentScreenDisplayed(pathname.includes('user-consents'), isSupportDeclineAllButton())
    );
  }, [platform]);

  useEffect(() => {
    setIsRestOfWorld(consentsLocale?.country !== 'us' ? true : false);
  }, [consentsLocale]);

  useEffect(() => {
    if (alpacaData !== null && !isRenderAcceptAllButton && userConsents) {
      renderTrackEvent(ffAnalyticsTracking);
      setRenderAcceptAllButton(true);
    }
  }, [alpacaData]);

  const redirectTo = () => {
    pathname.includes('user-consents')
      ? history.push(`/in-app/${platform}/${version}/user-consents-update${search}&onBoard=true`)
      : history.push(
          `/in-app/${platform}/${version}/printer-consents-update${search}&onBoard=true`
        );
  };

  const isSupportDeclineAllButton = () => !isWorkFromHomePrinter && !isCompanyTenantPrinter;

  const getButtonContainer = () => {
    if (reTarget) {
      return (
        <ReTargetButtonContainer
          data-testid="redirect-container"
          isDesktop={isDesktop}
          isRestOfWorld={isRestOfWorld}
          isRTL={isRTL}
          id="re-target-button-container"
        >
          <StyledButton
            onClick={handleClick}
            loading={loading}
            disabled={loading}
            small={smallButton}
            appearance={'primary'}
            id="consent-accept-all-button"
          >
            {acceptAllButton}
          </StyledButton>
          {isSupportDeclineAllButton() && (
            <StyledButton
              onClick={declineAllClick}
              appearance={'primary'}
              loading={declineAllLoading}
              disabled={declineAllLoading}
              small={smallButton}
              id="consent-decline-all-button"
            >
              {declineAllButton}
            </StyledButton>
          )}
          {!isWorkFromHomePrinter && !isCompanyTenantPrinter && !hasAppNameWJA && (
            <StyledButton
              data-testid="redirect-btn"
              onClick={redirectTo}
              appearance={hasAppNameEasyStart && !isRestOfWorld ? 'secondary' : 'tertiary'}
              small={smallButton}
              id="consent-manage=options-button"
            >
              {manageOptionsButton}
            </StyledButton>
          )}
        </ReTargetButtonContainer>
      );
    }

    return (
      <StyledButton
        onClick={handleClick}
        loading={loading}
        disabled={loading}
        small={smallButton}
        id="consent-button"
      >
        {hasAppNameSureSupply ? continueButtonText : continueButton}
      </StyledButton>
    );
  };

  const getWelcomeContainer = () => {
    if (reTarget) {
      return (
        <ReTargetContainer
          id="welcome-container"
          data-testid="welcome-container-retarget"
          hasBody={Boolean(body)}
          reTarget={reTarget}
          isDesktop={isDesktop}
          isRTL={isRTL}
        >
          <ReTargetContentContainer
            id="content-container"
            isDeclineAllButtonAppeared={
              isRestOfWorld && !isWorkFromHomePrinter && !isCompanyTenantPrinter
            }
            isCompanyTenantPrinter={isCompanyTenantPrinter || hasAppNameWJA}
          >
            {!isCompanyTenantPrinter && (
              <Graphic
                id="graphic"
                src={(() => {
                  if (hasAppNameEasyStart) {
                    return svg;
                  } else if (hasAppNameEasyStartPro) {
                    return easyStartproIcon;
                  } else if (hasAppNameWJA) {
                    return printerImage;
                  } else if (isWorkFromHomePrinter) {
                    return printerImage;
                  } else {
                    return svg;
                  }
                })()}
                alt="Printer and tablet line art"
                isWJA={hasAppNameWJA}
              />
            )}
            {body && alpacaData !== null && (
              <Title id="title-text" isDesktop={isDesktop}>
                {title}
              </Title>
            )}
            {body && alpacaData !== null && (
              <ReTargetBody id="hpsmart-content" reTarget={reTarget}>
                {body && body.match(/<[a-z]+>/) ? (
                  <div>
                    <span dangerouslySetInnerHTML={{ __html: body }} />
                    {!isRestOfWorld ? (
                      <BodyList
                        dangerouslySetInnerHTML={{ __html: secondaryBodyText }}
                        isRTL={isRTL}
                      />
                    ) : (
                      <BodyList
                        dangerouslySetInnerHTML={{ __html: supplementalText }}
                        isRTL={isRTL}
                      />
                    )}
                  </div>
                ) : (
                  <div>{body}</div>
                )}
              </ReTargetBody>
            )}
            {errorText && errorCode === 400 && (
              <ReTargetBody id="hpsmart-content" reTarget={reTarget}>
                {errorText && errorText.match(/<[a-z]+>/) ? (
                  <div>
                    <span dangerouslySetInnerHTML={{ __html: errorText }} />
                  </div>
                ) : (
                  <div style={{ textTransform: 'capitalize', fontSize: '2.975rem' }}>
                    {errorText}
                  </div>
                )}
              </ReTargetBody>
            )}
            {errorMessage && errorCode === 500 && (
              <ReTargetBody id="hpsmart-content" reTarget={reTarget}>
                {errorMessage && errorMessage.match(/<[a-z]+>/) ? (
                  <div>
                    <span dangerouslySetInnerHTML={{ __html: errorMessage }} />
                  </div>
                ) : (
                  <div style={{ textTransform: 'capitalize', fontSize: '2.975rem' }}>
                    {errorMessage}
                  </div>
                )}
              </ReTargetBody>
            )}
          </ReTargetContentContainer>
          {alpacaData !== null && getButtonContainer()}
        </ReTargetContainer>
      );
    }

    if (hasAppNameSureSupply) {
      return (
        <SureSupplyContainer id="suresupply-main-container">
          <LogoContainer id="logo-container">
            <LogoHp appearance="multicolor" size={100} />
          </LogoContainer>
          <BodySureSupply id="suresupply-content">
            <div dangerouslySetInnerHTML={{ __html: sureSupplyAnalyticsContent }} />
          </BodySureSupply>
          <ToggleLayout>
            <ToggleLabelHeader>{buttonTitle}</ToggleLabelHeader>
            <Toggle
              id="basic-suresupply-toggle"
              data-testid="setsuresupply-toggle"
              on={sureSupplyAnalyticsConsentToggle}
              onChange={(e) => {
                setSureSupplyAnalyticsConsentToggle(e);
              }}
            />
          </ToggleLayout>
          {getButtonContainer()}
        </SureSupplyContainer>
      );
    }

    return (
      <Container
        data-testid="welcome-container"
        id="welcome-container"
        hasBody={Boolean(body)}
        reTarget={reTarget}
        isRTL={isRTL}
      >
        <Graphic id="graphic" src={svg} alt="Printer and tablet line art" />
        <Title id="title-text">{title}</Title>
        {!reTarget && (
          <Body id="external-content">
            {alpacaData && alpacaData.match(/<[a-z]+>/) ? (
              <span dangerouslySetInnerHTML={{ __html: alpacaData }} />
            ) : (
              (alpacaData,
              (
                <div
                  id="welcome-text"
                  dangerouslySetInnerHTML={{ __html: requiredWelcomeContent }}
                />
              ))
            )}
          </Body>
        )}
        {body && (
          <Body id="hpsmart-content" reTarget={reTarget}>
            {body && body.match(/<[a-z]+>/) ? (
              <span dangerouslySetInnerHTML={{ __html: body }} />
            ) : (
              <div>{body}</div>
            )}
          </Body>
        )}
        {getButtonContainer()}
      </Container>
    );
  };

  let requiredPrinterConsentState;

  const checkRequiredConsentState = (data) => {
    const consentValues = Object.values(data);
    consentValues.map((consentData) => {
      if (consentData.purposeId === requiredBasicPrinter) {
        requiredPrinterConsentState = consentData.state;
      }
    });
    return requiredPrinterConsentState;
  };

  if (alpacaData !== null) {
    checkRequiredConsentState(alpacaData);
  }

  if (!checkBasicFuncPrinter) {
    if (query.checkBasicFuncPrinter && query.redirectUrl) {
      window.location.replace(query.redirectUrl);
    } else {
      return getWelcomeContainer();
    }
  } else {
    if (requiredPrinterConsentState === 'OptedIn' && query.redirectUrl) {
      window.location.replace(query.redirectUrl);
    } else {
      return getWelcomeContainer();
    }
  }
};

export default SmartWelcome;
